import { Minus, Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { BottomSheet } from './BottomSheet';
import { IconButton } from './Button';
import styles from './StepperSheet.module.css';

export interface StepperSheetProps {
  open: boolean;
  title: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  /** Log or fractional steps: the −/+ buttons call this instead of adding `step` (the knob's own arrow-key sizes) */
  nudge?: (value: number, dir: 1 | -1) => number;
  /** Formats the big number */
  format?: (value: number) => string;
  onChange: (value: number) => void;
  onClose: () => void;
}

/** A −/+ stepper plus a numeric input, opened from a Readout (DESIGN_SYSTEM §3: "tapping it opens a stepper or keypad sheet"). */
export function StepperSheet({ open, title, value, min, max, step = 1, unit, nudge, format, onChange, onClose }: StepperSheetProps) {
  const inputId = useId();
  const [draft, setDraft] = useState<string | null>(null);

  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const commit = (v: number) => {
    const clamped = clamp(v);
    onChange(clamped);
    setDraft(null);
  };

  return (
    <BottomSheet
      open={open}
      title={title}
      onClose={() => {
        onClose();
        setDraft(null);
      }}
    >
      <div className={styles.body}>
        <div className={styles.row}>
          <IconButton icon={Minus} aria-label={`Decrease ${title}`} onClick={() => commit(nudge ? nudge(value, -1) : value - step)} disabled={value <= min} />
          <span className={`t-num ${styles.value}`}>
            {format ? format(value) : value}
            {unit}
          </span>
          <IconButton icon={Plus} aria-label={`Increase ${title}`} onClick={() => commit(nudge ? nudge(value, 1) : value + step)} disabled={value >= max} />
        </div>
        <label htmlFor={inputId} className="visually-hidden">
          {title}, {min} to {max}
        </label>
        <input
          id={inputId}
          className={styles.input}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={nudge ? 'any' : step}
          value={draft ?? String(value)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            const n = Number(draft ?? value);
            commit(Number.isFinite(n) ? n : value);
          }}
        />
      </div>
    </BottomSheet>
  );
}
